import React from "react";
import { Search, Users, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";
import { AttendeeItem } from "./types";

interface BookingAttendeesTabProps {
  availableSlots: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredAttendees: AttendeeItem[];
  handleAttendeeToggle: (attendeeId: string) => void;
  isCoach: boolean;
  isLoadingClients?: boolean;
  onTrialUpdated?: () => void;
}

export const BookingAttendeesTab = ({
  availableSlots,
  searchQuery,
  setSearchQuery,
  filteredAttendees,
  handleAttendeeToggle,
  isCoach,
  isLoadingClients = false,
}: BookingAttendeesTabProps) => {
  const { t } = useTranslation(["schedule", "bookingui"]);

  const sortedAttendees = [...filteredAttendees].sort((a, b) => {
    if (a.selected !== b.selected) {
      return a.selected ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
  // Le coach n'a pas accès à l'annuaire des adhérents et ne peut pas réserver :
  // il ne voit que les inscrits du cours et les séances d'essai (lecture seule,
  // hormis le fait de se positionner sur un essai).
  const attendees = isCoach
    ? sortedAttendees.filter(a => a.selected || a.isTrial)
    : sortedAttendees;
  const selectedCount = sortedAttendees.filter(a => a.selected).length;
  const hasReachedCapacity = availableSlots <= 0;

  return (
    <div className="space-y-4 min-w-0 overflow-hidden">
      <div className="flex items-center justify-between text-sm min-w-0">
        <span className="text-muted-foreground flex items-center min-w-0">
          <Users className="h-4 w-4 mr-1 shrink-0" />
          {t('availableSlots', { ns: 'schedule' })}
        </span>
        <div className="flex items-center gap-2">
          <Badge variant={availableSlots > 0 ? "outline" : "destructive"}>
            {availableSlots}
          </Badge>
          {selectedCount > 0 && (
            <Badge variant="default" className="flex items-center gap-1">
              <Check className="h-3 w-3" /> {selectedCount}
            </Badge>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('searchUsers', { ns: 'schedule' })}
          className="pl-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <ScrollArea className="h-[200px] rounded-md border p-2">
        <div className="space-y-2">
          {isLoadingClients ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">{t("bookingAttendeesTab.loadingClients", { ns: "bookingui" })}</p>
              </div>
            </div>
          ) : attendees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('noClientsFound', { ns: 'schedule', defaultValue: 'No clients found' })}
            </p>
          ) : (
            attendees.map((attendee) => {
              return (
                <div
                  key={attendee.id}
                  className={`flex items-start space-x-2 p-1.5 rounded-md min-w-0 ${
                    attendee.selected ? "bg-muted/50" : ""
                  } ${attendee.ineligible ? "opacity-70" : ""} ${attendee.isTrial ? "border border-amber-500/40 bg-amber-500/5" : ""}`}
                >
                  <Checkbox
                    checked={attendee.selected}
                    onCheckedChange={() => !attendee.isTrial && handleAttendeeToggle(attendee.id)}
                    disabled={!attendee.isSelectable || (!attendee.selected && hasReachedCapacity)}
                    id={`attendee-${attendee.id}`}
                    className="shrink-0 mt-0.5"
                  />
                  <label
                    htmlFor={`attendee-${attendee.id}`}
                    className={`text-sm leading-tight flex-1 min-w-0 ${
                      attendee.selected ? "font-semibold" : "font-medium"
                    }`}>
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      <span className="truncate">
                        {`${attendee.name}${attendee.clothingSize ? " (" + attendee.clothingSize + ")" : ""}`}
                      </span>
                      {attendee.isTrial && (
                        <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 border-amber-500/60 text-amber-600 dark:text-amber-400">
                          {t('trialBadge', { ns: 'schedule', defaultValue: "Séance d'essai" })}
                        </Badge>
                      )}
                      {attendee.ineligible && (
                        <Badge variant="destructive" className="shrink-0 text-[10px] px-1.5 py-0">
                          {t("bookingAttendeesTab.subscriptionExpired", { ns: "bookingui" })}
                        </Badge>
                      )}
                    </div>
                    {attendee.isTrial && attendee.trialPhone && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        📞 <a href={`tel:${attendee.trialPhone}`} className="underline">{attendee.trialPhone}</a>
                      </div>
                    )}
                    {attendee.isTrial && attendee.trialNotes && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 italic">
                        {attendee.trialNotes}
                      </div>
                    )}
                    {attendee.isTrial && attendee.trialCoachName && (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {t("bookingAttendeesTab.coachName", { ns: "bookingui", name: attendee.trialCoachName })}
                        </Badge>
                      </div>
                    )}
                  </label>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
